import _ from 'lodash'
import { cacher } from '@/utils/rtkQueryCacheUtils'

import { Network, NetworkDesmosAct, NetworkInvitation, NetworkMember, WaitingRoomActConfig } from '@/core/models/network.model'
import { WithId } from '@/core/types/shared'
import { SocketEvent } from '@/core/types/socket-event'

import { selectAuthUser } from '@/redux/reducers/auth.reducer'
import { RootState } from '@/redux/rootReducer'

import { Tag, baseApi } from './base'
import { getSocket } from './socket'
import { Consultation, PaymentType } from '../models/consultation.model'
import { SearchRequestOptions } from '../types/request'

type CreateNetworkParams = Pick<Network, 'description' | 'name' | 'privacy'>

type UpdateNetworkParams = WithId<Partial<CreateNetworkParams>>

export type GetNetworksParams = {
  createdBy?: string
  name?: string
}

export type MarkConsultationsAsPaidDto = {
  consultationIds: string[]
  price: number
  paymentType: PaymentType
}

export type ExportConsultationsDto = {
  startDateFrom?: string
  startDateTo?: string
  doctors?: string[]
  pds?: string[]
  paid?: boolean
  paymentType?: PaymentType
}

// Add this interface for the export response
export interface ExportResponse {
  fileName: string
  csvContent: string
}

export const networkApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getNetwork: builder.query<Network, string>({
      query: (networkId) => `/networks/${networkId}`,
      async onCacheEntryAdded(networkId, { dispatch, getState, updateCachedData, cacheDataLoaded, cacheEntryRemoved }) {
        const socket = await getSocket()
        const networkUpdatedListener = (updatedNetwork: Network) => {
          updateCachedData((cachedNetwork) => {
            if (cachedNetwork._id === updatedNetwork._id) {
              Object.assign(cachedNetwork, updatedNetwork)
            }
          })
        }
        const networkDeletedListener = (deletedNetwork: Network) => {
          if (deletedNetwork._id === networkId) {
            dispatch(networkApi.util.invalidateTags([{ type: Tag.NETWORK, id: networkId }]))
          }
        }
        try {
          const { data: network } = await cacheDataLoaded
          const authUser = selectAuthUser(getState() as RootState)
          // subscribe to network if auth user just "visits" the network
          if (!network.members.find((member) => member === authUser?._id)) {
            socket.emit(SocketEvent.SUBSCRIBE_NETWORKS, [networkId])
          }
          socket.on(SocketEvent.NETWORK_UPDATED, networkUpdatedListener)
          socket.on(SocketEvent.NETWORK_DELETED, networkDeletedListener)
        } catch { }
        await cacheEntryRemoved
        socket.emit(SocketEvent.UNSUBSCRIBE_NETWORKS, [networkId])
        socket.off(SocketEvent.NETWORK_UPDATED, networkUpdatedListener)
        socket.off(SocketEvent.NETWORK_DELETED, networkDeletedListener)
      },
      providesTags: (_result, _error, networkId) => [{ type: Tag.NETWORK, id: networkId }],
    }),
    getNetworkInvitations: builder.query<NetworkInvitation[], string>({
      query: (networkId) => ({
        url: `/networks/${networkId}/invitations`,
        method: 'GET',
      }),
      async onCacheEntryAdded(networkId, { cacheDataLoaded, cacheEntryRemoved, updateCachedData }) {
        const socket = await getSocket()
        const networkInvitationSentListener = (networkInvitation: NetworkInvitation) => {
          if (networkInvitation.network._id === networkId) {
            updateCachedData((cachedNetworkInvitations) => {
              cachedNetworkInvitations.push(networkInvitation)
            })
          }
        }
        const networkInvitationDeletedListener = (deletedNetworkInvitation: NetworkInvitation) => {
          if (deletedNetworkInvitation.network._id === networkId) {
            updateCachedData((cachedNetworkInvitations) => {
              return cachedNetworkInvitations.filter((networkInvitation) => networkInvitation._id !== deletedNetworkInvitation._id)
            })
          }
        }
        try {
          await cacheDataLoaded
          socket.on(SocketEvent.NETWORK_INVITATION_SENT, networkInvitationSentListener)
          socket.on(SocketEvent.NETWORK_INVITATION_DELETED, networkInvitationDeletedListener)
        } catch { }
        await cacheEntryRemoved
        socket.off(SocketEvent.NETWORK_INVITATION_SENT, networkInvitationSentListener)
        socket.off(SocketEvent.NETWORK_INVITATION_DELETED, networkInvitationDeletedListener)
      },
      providesTags: cacher.providesList(Tag.NETWORK_INVITATION),
    }),
    getNetworkMembers: builder.query<NetworkMember[], string>({
      query: (networkId) => `/networks/${networkId}/members`,
      async onCacheEntryAdded(networkId, { updateCachedData, cacheDataLoaded, cacheEntryRemoved }) {
        const socket = await getSocket()
        const networkMemberCreatedListener = (networkMember: NetworkMember) => {
          updateCachedData((cachedNetworkMembers) => {
            cachedNetworkMembers.push(networkMember)
          })
        }
        const networkMemberUpdatedListener = (updatedNetworkMember: NetworkMember) => {
          updateCachedData((cachedNetworkMembers) => {
            const index = cachedNetworkMembers.findIndex(
              (member) => member.member._id === updatedNetworkMember.member._id && member.network === updatedNetworkMember.network
            )
            if (index !== -1) {
              cachedNetworkMembers[index] = updatedNetworkMember
            }
          })
        }
        const networkMemberDeletedListener = (networkId: string, removedMembers: string[]) => {
          updateCachedData((cachedNetworkMembers) => {
            return cachedNetworkMembers.filter((networkMember) => !(networkMember.network === networkId && removedMembers.includes(networkMember.member._id)))
          })
        }
        try {
          await cacheDataLoaded
          socket.on(SocketEvent.NETWORK_MEMBER_CREATED, networkMemberCreatedListener)
          socket.on(SocketEvent.NETWORK_MEMBER_UPDATED, networkMemberUpdatedListener)
          socket.on(SocketEvent.NETWORK_MEMBERS_DELETED, networkMemberDeletedListener)
        } catch { }
        await cacheEntryRemoved
        socket.off(SocketEvent.NETWORK_MEMBER_CREATED, networkMemberCreatedListener)
        socket.off(SocketEvent.NETWORK_MEMBER_UPDATED, networkMemberUpdatedListener)
        socket.off(SocketEvent.NETWORK_MEMBERS_DELETED, networkMemberDeletedListener)
      },
      providesTags: cacher.providesList(Tag.NETWORK_MEMBER),
    }),
    getNetworks: builder.query<Network[], GetNetworksParams>({
      query: (params) => ({
        url: '/networks',
        method: 'GET',
        params,
      }),
      async onCacheEntryAdded(_arg, { cacheDataLoaded, cacheEntryRemoved, updateCachedData }) {
        const socket = await getSocket()
        const networkUpdatedListener = (updatedNetwork: Network) => {
          updateCachedData((draftNetworks) => {
            for (const drafNetwork of draftNetworks) {
              if (drafNetwork._id !== updatedNetwork._id) continue
              Object.assign(drafNetwork, updatedNetwork)
            }
          })
        }
        try {
          await cacheDataLoaded
          socket.on(SocketEvent.NETWORK_UPDATED, networkUpdatedListener)
        } catch { }
        await cacheEntryRemoved
        socket.off(SocketEvent.NETWORK_UPDATED, networkUpdatedListener)
      },
      providesTags: cacher.providesList(Tag.NETWORK),
    }),
    discoverNetworks: builder.query<Network[], { limit?: number; skip: number }>({
      query: ({ limit = 20, skip }) => ({
        url: '/networks/discover',
        method: 'GET',
        params: { limit, skip },
      }),
      async onCacheEntryAdded(_arg, { cacheDataLoaded, cacheEntryRemoved, updateCachedData }) {
        let networkIds: string[] = []
        const socket = await getSocket()
        const networkUpdatedListener = (updatedNetwork: Network) => {
          updateCachedData((cachedNetworks) => {
            for (const cachedNetwork of cachedNetworks) {
              if (cachedNetwork._id !== updatedNetwork._id) continue
              Object.assign(cachedNetwork, updatedNetwork)
            }
          })
        }
        try {
          const { data: networks } = await cacheDataLoaded
          networkIds = networks.map((network) => network._id)
          socket.on(SocketEvent.NETWORK_UPDATED, networkUpdatedListener)
          socket.emit(SocketEvent.SUBSCRIBE_NETWORKS, networkIds)
        } catch { }
        await cacheEntryRemoved
        socket.on(SocketEvent.NETWORK_UPDATED, networkUpdatedListener)
        socket.emit(SocketEvent.UNSUBSCRIBE_NETWORKS, networkIds)
      },
    }),
    getJoinedNetworks: builder.query<Network[], { cacheKey: string; limit?: number; skip: number }>({
      query: ({ limit = 20, skip }) => ({
        url: '/networks/joins',
        method: 'GET',
        params: { limit, skip },
      }),
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        return endpointName + queryArgs.cacheKey
      },
      merge: (currentCache, newItems) => {
        currentCache.push(...newItems)
      },
      forceRefetch({ currentArg, previousArg }) {
        return currentArg !== previousArg
      },
      async onCacheEntryAdded(arg, { cacheDataLoaded, cacheEntryRemoved, updateCachedData }) {
        const socket = await getSocket()
        const networkUpdatedListener = (updatedNetwork: Network) => {
          updateCachedData((cachedNetworks) => {
            for (const cachedNetwork of cachedNetworks) {
              if (cachedNetwork._id !== updatedNetwork._id) continue
              Object.assign(cachedNetwork, updatedNetwork)
            }
          })
        }
        try {
          await cacheDataLoaded
          socket.on(SocketEvent.NETWORK_UPDATED, networkUpdatedListener)
        } catch { }
        await cacheEntryRemoved
        socket.off(SocketEvent.NETWORK_UPDATED, networkUpdatedListener)
      },
    }),
    createNetwork: builder.mutation<Network, CreateNetworkParams>({
      query: (body) => ({
        url: '/networks',
        method: 'POST',
        body,
      }),
      invalidatesTags: cacher.invalidatesList(Tag.NETWORK),
    }),
    updateNetwork: builder.mutation<Network, UpdateNetworkParams>({
      query: ({ id: networkId, ...body }) => ({
        url: `/networks/${networkId}`,
        method: 'PATCH',
        body,
      }),
    }),
    deleteNetwork: builder.mutation<Network, string>({
      query: (networkId) => ({
        url: `/networks/${networkId}`,
        method: 'DELETE',
      }),
      invalidatesTags: cacher.invalidatesList(Tag.NETWORK),
    }),
    leaveNetwork: builder.mutation<Network, string>({
      query: (networkId) => ({
        url: `/networks/${networkId}/leave`,
        method: 'PATCH',
      }),
      async onQueryStarted(networkId, { dispatch, queryFulfilled }) {
        try {
          const { data: updatedNetwork } = await queryFulfilled
          dispatch(
            networkApi.util.updateQueryData('getNetwork', networkId, (cachedNetwork) => {
              Object.assign(cachedNetwork, updatedNetwork)
            })
          )
        } catch { }
      },
    }),
    sendNetworkInvitation: builder.mutation<Network, { networkId: string; emails?: string[]; staffs?: string[] }>({
      query: ({ networkId, emails, staffs }) => ({
        url: `/networks/${networkId}/invite`,
        method: 'POST',
        body: { emails, staffs },
      }),
      invalidatesTags: cacher.invalidatesList(Tag.NETWORK_INVITATION),
    }),
    replyNetworkInvitation: builder.mutation<Network, { networkId: string; action: 'accept' | 'decline' }>({
      query: ({ networkId, action }) => ({
        url: `/networks/${networkId}/invitations`,
        method: 'PATCH',
        body: { action },
      }),
      invalidatesTags: (_result, _error, arg) => (arg.action === 'decline' ? cacher.invalidatesList(Tag.NETWORK_INVITATION)() : []),
    }),
    sendNetworkJoinRequest: builder.mutation<Network, string>({
      query: (networkId) => ({
        url: `/networks/${networkId}/join`,
        method: 'POST',
      }),
      async onQueryStarted(networkId, { dispatch, queryFulfilled }) {
        try {
          const { data: updatedNetwork } = await queryFulfilled
          dispatch(
            networkApi.util.updateQueryData('getNetwork', networkId, (cachedNetwork) => {
              Object.assign(cachedNetwork, updatedNetwork)
            })
          )
        } catch { }
      },
    }),
    replyNetworkJoinRequest: builder.mutation<Network, { networkId: string; action: 'accept' | 'cancel' | 'decline'; applicant: string }>({
      query: ({ networkId, action, applicant }) => ({
        url: `/networks/${networkId}/join-requests`,
        method: 'PATCH',
        body: { action, applicant },
      }),
      invalidatesTags: (_result, _error, arg) => [{ type: Tag.NETWORK, id: arg.networkId }],
    }),
    removeNetworkMembers: builder.mutation<Network, { networkId: string; members: string[] }>({
      query: ({ networkId, members }) => ({
        url: `/networks/${networkId}/members/remove`,
        method: 'PATCH',
        body: { members },
      }),
      invalidatesTags: (_result, _error, arg) => [{ type: Tag.NETWORK, id: arg.networkId }, ...cacher.invalidatesList(Tag.NETWORK_MEMBER)()],
    }),
    uploadNetworkCover: builder.mutation<Network, { networkId: string; file: File }>({
      query: ({ networkId, file }) => {
        const body = new FormData()
        body.append('file', file)
        return {
          url: `/networks/${networkId}/upload-cover`,
          method: 'PATCH',
          body,
        }
      },
      invalidatesTags: (_result, _error, { networkId }) => [{ type: Tag.NETWORK, id: networkId }],
    }),
    updateNetworkMember: builder.mutation<NetworkMember, { networkId: string; body: Partial<NetworkMember> }>({
      query: ({ networkId, body }) => ({
        url: `/networks/${networkId}/members/me`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_result, _error, { networkId }) => [{ type: Tag.NETWORK_MEMBER, id: networkId }, ...cacher.invalidatesList(Tag.NETWORK_MEMBER)()],
    }),
    updateMemberAccessLevel: builder.mutation<NetworkMember, { networkId: string; memberId: string; accessLevel: string }>({
      query: ({ networkId, memberId, accessLevel }) => ({
        url: `/networks/${networkId}/members/${memberId}/access-level`,
        method: 'PATCH',
        body: { accessLevel },
      }),
      invalidatesTags: (_result, _error, { networkId }) => [{ type: Tag.NETWORK_MEMBER, id: networkId }, ...cacher.invalidatesList(Tag.NETWORK_MEMBER)()],
    }),
    markConsultationsAsPaid: builder.mutation<Consultation[], { networkId: string; waitingRoomId: string; body: MarkConsultationsAsPaidDto }>({
      query: ({ networkId, waitingRoomId, body }) => ({
        url: `/networks/${networkId}/waiting-rooms/${waitingRoomId}/consultations/mark-as-paid`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, { body }) => [
        { type: Tag.CONSULTATION, id: 'LIST' },
        ...body.consultationIds.map((id) => ({ type: Tag.CONSULTATION as const, id })),
      ],
    }),

    queryWaitingRoomConsultations: builder.query<
      [Consultation[], number],
      { networkId: string; waitingRoomId: string; body: { query: any; options: SearchRequestOptions } }
    >({
      query: ({ networkId, waitingRoomId, body }) => ({
        url: `/networks/${networkId}/waiting-rooms/${waitingRoomId}/consultations/query`,
        method: 'POST',
        body,
      }),
      providesTags: (result) =>
        result
          ? [...result[0].map(({ _id }) => ({ type: Tag.CONSULTATION as const, id: _id })), { type: Tag.CONSULTATION as const, id: 'LIST' }]
          : [{ type: Tag.CONSULTATION as const, id: 'LIST' }],
    }),

    convertMeetingLinkToConsultation: builder.mutation<void, { networkId: string; orderId: string }>({
      query: ({ networkId, orderId }) => ({
        url: `/networks/${networkId}/meeting-link-orders/convert-to-consultation`,
        method: 'POST',
        body: { orderId },
      }),
      invalidatesTags: [],
    }),

    convertMeetingLinkToExpertise: builder.mutation<void, { networkId: string; orderId: string }>({
      query: ({ networkId, orderId }) => ({
        url: `/networks/${networkId}/meeting-link-orders/convert-to-expertise`,
        method: 'POST',
        body: { orderId },
      }),
      invalidatesTags: [],
    }),
    getWaitingRoomConsultationsSummary: builder.query<
      { total: number; paidCount: number; revenue: number; pendingCount: number; issueCount: number; completedCount: number },
      { networkId: string; waitingRoomId: string; startDateFrom?: string; startDateTo?: string }
    >({
      query: ({ networkId, waitingRoomId, startDateFrom, startDateTo }) => ({
        url: `/networks/${networkId}/waiting-rooms/${waitingRoomId}/consultations/summary`,
        params: { ...(startDateFrom ? { startDateFrom } : {}), ...(startDateTo ? { startDateTo } : {}) },
      }),
      providesTags: (_result, _error, { waitingRoomId }) => [{ type: Tag.CONSULTATION as const, id: `summary-${waitingRoomId}` }],
    }),

    exportWaitingRoomConsultations: builder.query<ExportResponse, { networkId: string; waitingRoomId: string; params: ExportConsultationsDto }>({
      query: ({ networkId, waitingRoomId, params }) => ({
        url: `/networks/${networkId}/waiting-rooms/${waitingRoomId}/consultations/export`,
        method: 'GET',
        params: params, // Let RTK Query handle params conversion
      }),
    }),

    getNetworkDesmosConfig: builder.query<{ waitingRoomActConfigs: NetworkDesmosAct[]; desmosActs: NetworkDesmosAct[] }, string>({
      query: (networkId) => `/networks/${networkId}/desmos-config`,
      providesTags: (_result, _error, networkId) => [{ type: Tag.NETWORK, id: networkId }],
    }),

    updateNetworkDesmosConfig: builder.mutation<{ waitingRoomActConfigs: NetworkDesmosAct[]; desmosActs: NetworkDesmosAct[] }, { networkId: string; body: { acts: NetworkDesmosAct[] } }>({
      query: ({ networkId, body }) => ({
        url: `/networks/${networkId}/desmos-config`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_result, _error, { networkId }) => [{ type: Tag.NETWORK, id: networkId }],
    }),

    getNetworkActConfig: builder.query<{ waitingRoomActConfigs: WaitingRoomActConfig[] }, string>({
      query: (networkId) => `networks/${networkId}/act-config`,
      providesTags: (_result, _error, networkId) => [{ type: Tag.NETWORK, id: `act-config-${networkId}` }],
    }),

    updateNetworkActConfig: builder.mutation<{ waitingRoomActConfigs: WaitingRoomActConfig[] }, { networkId: string; acts: WaitingRoomActConfig[] }>({
      query: ({ networkId, acts }) => ({
        url: `networks/${networkId}/act-config`,
        method: 'PATCH',
        body: { acts },
      }),
      invalidatesTags: (_result, _error, { networkId }) => [{ type: Tag.NETWORK, id: `act-config-${networkId}` }],
    }),

    getAvailableActs: builder.query<{ acts: any[] }, string>({
      query: (consultationId) => `consultations/${consultationId}/available-acts`,
    }),
  }),
})
